import { useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Header from "components/Header";
import { CARRIER_FR_STATUS_LABELS } from "constants/base";
import { routes } from "constants/routes";
import AssignedLoadItem from "features/main/HaulPay/Shared/AssignedLoadItem";
import MainLayout from "layouts/Main";
import { IFundingRequestItem } from "models/HaulPay";
import styles from "./style.module.scss";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import { QUERY_CARRIER_FUNDING_REQUEST_BY_ME } from "../../ManagePay/query";
import LoadingSpinner from "components/LoadingSpinner";
import SearchInput from "../../../modules/FRListByStatusDesktop/components/SearchInput";

type Filters = {
  loadOrInvoiceOrDebtorOrStatus?: string | undefined;
  orderBy?: string;
  status?: string;
  assignedLoads?: boolean;
};

type FiltersUpdate = Partial<Filters>;

export default function AssignedLoadsListByStatusMobile() {
  const navigate = useNavigate();
  const { status } = useParams();
  const ContainerRef = useRef<HTMLDivElement>(null);
  const pageTitle = status ? CARRIER_FR_STATUS_LABELS[status] : "";

  const [filters, setFilters] = useState<Filters>({
    loadOrInvoiceOrDebtorOrStatus: "",
    orderBy: "-created",
    status: status === "all" ? undefined : status,
    assignedLoads: true
  });

  const updateFilters = useCallback(
    (updates: FiltersUpdate) => {
      setFilters((prevFilters) => {
        const hasChanges = Object.keys(updates).some((key) => {
          const typedKey = key as keyof Filters;
          return prevFilters[typedKey] !== updates[typedKey];
        });

        if (!hasChanges) {
          return prevFilters;
        }

        return {
          ...prevFilters,
          ...updates
        };
      });
    },
    [setFilters]
  );

  const handleSearch = useCallback(
    (value: string | null | undefined) => {
      updateFilters({ loadOrInvoiceOrDebtorOrStatus: value ?? "", status: undefined });
    },
    [updateFilters]
  );

  const { data, loading, loadNextPage, currentPage } = usePaginatedQuery<
    Record<string, any>,
    IFundingRequestItem
  >({
    query: QUERY_CARRIER_FUNDING_REQUEST_BY_ME,
    variables: filters,
    cursorField: "fundingRequestsByMe",
    pageSize: 20,
    options: {
      notifyOnNetworkStatusChange: true
    }
  });
  const isInitialLoading = loading && currentPage === 1;
  const isLoadingMore = loading && currentPage > 1;
  const goToMoreDetails = (id: string) => {
    navigate(`${routes.HAULPAY_ASSIGNED_LOADS_PAYMENT_DETAILS}/${id}`);
  };

  const onScroll = async () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (!!scrollTop && scrollTop + offsetHeight + 1 >= scrollHeight && isLoadingMore) {
        loadNextPage();
      }
    }
  };
  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner isFetching={isInitialLoading} />
      </div>
    );
  }
  return (
    <MainLayout customHeader={<Header label={pageTitle} />} className="px-5 pb-24">
      <div className="flex-1" ref={ContainerRef} onScroll={onScroll}>
        <SearchInput
          initialValue={filters.loadOrInvoiceOrDebtorOrStatus || ""}
          onSearch={(value) => handleSearch(value ?? "")}
          isMobile={true}
          iconClassName={styles.searchIcon}
        />

        <div className="flex flex-col gap-4 mt-4">
          {data.map((item) => (
            <AssignedLoadItem
              key={item.id}
              data={item}
              goToMoreDetails={() => goToMoreDetails(item.id)}
            />
          ))}
        </div>
      </div>
    </MainLayout>
  );
}
